import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import PresentationFields, {
  toFormValues,
  mergePresentation,
  parsePresentationErrors,
} from '../presentation/PresentationFields'
import PathwayIcon from '../presentation/PathwayIcon'

/**
 * Edits the intervention-level presentation overrides
 * (intervention_config_tracks.presentation: display_name, description,
 * icon) for one attached pathway. Blank fields inherit the library
 * pathway, then the type default; both are shown as placeholders.
 *
 * `onSave(presentation)` must return a promise; a rejection with a
 * data-service validation message is shown inline.
 */
function PresentationOverrideDialog({
  open,
  onClose,
  pathway,
  libraryPresentation,
  typeDefaults,
  onSave,
  saving,
}) {
  const [form, setForm] = useState(toFormValues(null))
  const [fieldErrors, setFieldErrors] = useState({})
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open && pathway) {
      setForm(toFormValues(pathway.presentation))
      setFieldErrors({})
      setError(null)
    }
  }, [open, pathway])

  if (!pathway) return null

  const library = libraryPresentation || {}
  const placeholders = {
    display_name: library.display_name || pathway.name || '',
    description: library.description || '',
    icon: library.icon || typeDefaults?.icon || 'compass',
  }
  const resolved = {
    display_name: form.display_name.trim() || placeholders.display_name,
    description: form.description.trim() || placeholders.description,
    icon: form.icon || placeholders.icon,
  }
  const hasOverrides = Boolean(form.display_name.trim() || form.description.trim() || form.icon)

  const handleSave = async () => {
    setError(null)
    setFieldErrors({})
    try {
      await onSave(mergePresentation(pathway.presentation, form))
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || err.message
      const parsed = parsePresentationErrors(message)
      setFieldErrors(parsed)
      if (Object.keys(parsed).length === 0) {
        setError(message || 'Failed to save presentation')
      }
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        App Presentation
        <Typography variant="body2" color="text.secondary">
          {pathway.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How this pathway appears to patients in this intervention. Blank fields inherit
          the library pathway, then the type default (shown as placeholders).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <PresentationFields
          value={form}
          onChange={setForm}
          placeholders={placeholders}
          errors={fieldErrors}
          disabled={saving}
        />

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ mt: 2.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}
        >
          <PathwayIcon name={resolved.icon} size={24} />
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {resolved.display_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {resolved.description || `${typeDefaults?.label || pathway.track_type} · no description`}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setForm(toFormValues(null))}
          disabled={saving || !hasOverrides}
          sx={{ mr: 'auto' }}
        >
          Clear overrides
        </Button>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PresentationOverrideDialog
